// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/forms/-/auth/new-password/:token\",\"regex\":\"^/forms/-/auth/new-password/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/forms/-/documents/:requestId\",\"regex\":\"^/forms/-/documents/([^/]+)$\",\"paramNames\":[\"requestId\"],\"hasOptionalPrefix\":false},{\"path\":\"/forms/-/documents/:requestId/success\",\"regex\":\"^/forms/-/documents/([^/]+)/success$\",\"paramNames\":[\"requestId\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/forms\"},{\"path\":\"/forms/-/auth/phone-verify\"},{\"path\":\"/forms/-/auth/user-info\"},{\"path\":\"/forms/-/auth/create\"},{\"path\":\"/forms/-/auth/create-success\"},{\"path\":\"/forms/-/auth/password\"},{\"path\":\"/forms/-/auth/reset-password\"},{\"path\":\"/forms/-/auth/reset-password-verification\"},{\"path\":\"/forms/-/auth/start\"},{\"path\":\"/forms/-/booking\"},{\"path\":\"/forms/-/booking/cancelled\"},{\"path\":\"/forms/-/booking/company\"},{\"path\":\"/forms/-/booking/confirm\"},{\"path\":\"/forms/-/booking/end\"},{\"path\":\"/forms/-/booking/location\"},{\"path\":\"/forms/-/booking/time\"},{\"path\":\"/forms/-/checkout\"},{\"path\":\"/forms/-/checkout/business-information\"},{\"path\":\"/forms/-/checkout/buy-type\"},{\"path\":\"/forms/-/checkout/buyers-information\"},{\"path\":\"/forms/-/checkout/byo-finance\"},{\"path\":\"/forms/-/checkout/confirmation\"},{\"path\":\"/forms/-/checkout/extended-coverage\"},{\"path\":\"/forms/-/checkout/finance-quote-info\"},{\"path\":\"/forms/-/checkout/finance-quotes-list\"},{\"path\":\"/forms/-/checkout/funding-information\"},{\"path\":\"/forms/-/checkout/handover-information\"},{\"path\":\"/forms/-/checkout/summary\"},{\"path\":\"/forms/-/checkout/expired\"},{\"path\":\"/forms/-/personalised-quote\"},{\"path\":\"/forms/-/trade-in-enquiry\"},{\"path\":\"/forms/-/trade-in-enquiry/accident-details\"},{\"path\":\"/forms/-/trade-in-enquiry/basic-info\"},{\"path\":\"/forms/-/trade-in-enquiry/car-history\"},{\"path\":\"/forms/-/trade-in-enquiry/digital-screen\"},{\"path\":\"/forms/-/trade-in-enquiry/edit-car\"},{\"path\":\"/forms/-/trade-in-enquiry/expected-valuation\"},{\"path\":\"/forms/-/trade-in-enquiry/exterior\"},{\"path\":\"/forms/-/trade-in-enquiry/exterior-damage\"},{\"path\":\"/forms/-/trade-in-enquiry/factory-options\"},{\"path\":\"/forms/-/trade-in-enquiry/hail\"},{\"path\":\"/forms/-/trade-in-enquiry/interior\"},{\"path\":\"/forms/-/trade-in-enquiry/interior-damage\"},{\"path\":\"/forms/-/trade-in-enquiry/intro\"},{\"path\":\"/forms/-/trade-in-enquiry/mechanical\"},{\"path\":\"/forms/-/trade-in-enquiry/mechanical-issues\"},{\"path\":\"/forms/-/trade-in-enquiry/missed-anything\"},{\"path\":\"/forms/-/trade-in-enquiry/modifications\"},{\"path\":\"/forms/-/trade-in-enquiry/odours\"},{\"path\":\"/forms/-/trade-in-enquiry/other-fields\"},{\"path\":\"/forms/-/trade-in-enquiry/personal-info\"},{\"path\":\"/forms/-/trade-in-enquiry/photos\"},{\"path\":\"/forms/-/trade-in-enquiry/rego-state\"},{\"path\":\"/forms/-/trade-in-enquiry/rips-tears\"},{\"path\":\"/forms/-/trade-in-enquiry/service-history\"},{\"path\":\"/forms/-/trade-in-enquiry/service-history-records\"},{\"path\":\"/forms/-/trade-in-enquiry/stains\"},{\"path\":\"/forms/-/trade-in-enquiry/timeline\"},{\"path\":\"/forms/-/trade-in-enquiry/tyre\"},{\"path\":\"/forms/-/trade-in-enquiry/vehicle-confirmation\"},{\"path\":\"/forms/-/trade-in-enquiry/wheel-damage\"},{\"path\":\"/forms/-/trade-in-enquiry/windscreen-damage\"},{\"path\":\"/forms/-/trade-in-enquiry/car-found\"},{\"path\":\"/forms/-/trade-in-enquiry/car-not-found\"},{\"path\":\"/forms/-/trade-in-enquiry/continue\"},{\"path\":\"/forms/-/trade-in-enquiry/end\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.11";import * as Sentry from '@sentry/nextjs';

import { getSampleRate, getSentryDsn } from '@/sentry.helpers';

const dsn = getSentryDsn();

const config: Sentry.BrowserOptions = {
  dsn,

  environment: process.env.SENTRY_ENVIRONMENT,

  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampler: (samplingContext) => {
    const { attributes } = samplingContext;
    // Check if URL contains /forms and set 100% sampling rate
    if (
      (typeof attributes?.path === 'string' && attributes.path.includes('/forms')) ||
      (typeof window !== 'undefined' && window.location.href.includes('/forms'))
    ) {
      return 1.0;
    }

    return getSampleRate(0.02);
  },
  // Profiling sample rate is relative to tracesSampleRate
  profilesSampleRate: 1.0,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  enabled: Boolean(dsn),

  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.0,
  integrations: [],

  beforeSend: (event, hint) => {
    const error = hint.originalException;
    // Filter out hard navigate errors from NextJS
    // https://sentry.io/answers/handle-hard-navigation-errors-in-nextjs/
    if (event.exception?.values?.[0]?.value?.includes('Invariant: attempted to hard navigate to the same URL')) {
      return null;
    }

    // Check for the specific error type and message
    if (
      error &&
      typeof error === 'object' &&
      'name' in error &&
      'message' in error &&
      error.name === 'TypeError' &&
      error.message === 'Load failed'
    ) {
      return null;
    }

    return event;
  },

  // See https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/filtering/#decluttering-sentry
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Ignore fetch errors, most of them happen due to slow network
    'TypeError: Failed to fetch',
    'TypeError: NetworkError when attempting to fetch resource.',
    'Network Error',
    // Facebook iOS autofill not properly implemented
    '_AutofillCallbackHandler',
    // Hubspot tracking
    '_hsq',
    // React internal error thrown when something outside react modifies the DOM
    // This is usually because of a browser extension or Chrome's built-in translate
    "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
    "NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
    // Block CSP error from tracking scripts
    "Blocked unsafe (eval() or inline) 'script'",
    "Blocked 'script' from 'inline:'",
    "Blocked 'script' from 'eval:'",
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i,
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    // Ignore Google flakiness
    /\/(gtm|ga|analytics)\.js/i,
    // Ignore LiveChat network errors
    /\/tracking\.js/i,
    // Ignore Google One-tap errors
    /gsi\/client/i,
    // Ignore Google tag manager errors
    /gtag\/js/i,
    // Ignore Recaptcha errors
    /recaptcha__en/i,
    // Ignore Userberry errors
    /useberryScript/i,
    // Ignore maze.co errors
    /contextualScaffold/i,
    // Ignore bing errors
    /bat\.bing\.com/i,
  ],
};

Sentry.init({
  ...config,
  integrations: [
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: [process.env.SENTRY_APPLICATION_KEY ?? ''],
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
    }),
  ],
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
